import { useEffect, useRef, useState } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { setTelemetryRoute, track } from './lib/telemetry';
import { signOut } from 'firebase/auth';
import { AppShell } from './layouts/AppShell';
import { Login } from './pages/Login';
import { RoleDashboard } from './pages/RoleDashboard';
import { ProjectDashboard } from './pages/ProjectDashboard';
import { Settings } from './pages/Settings';
import { AcceptInvite } from './pages/AcceptInvite';
import { ResetPassword } from './pages/ResetPassword';
import { OAuthCallback } from './pages/OAuthCallback';
import { PortfolioPage } from './pages/placeholders/PortfolioPage';
import { MyWorkPage } from './pages/placeholders/MyWorkPage';
import { InsightsPage } from './pages/placeholders/InsightsPage';
import { ReportsPage } from './pages/placeholders/ReportsPage';
import { TeamPage } from './pages/placeholders/TeamPage';
import { LaunchesPage } from './pages/placeholders/LaunchesPage';
import { Support } from './pages/Support';
import { useAuth } from './hooks/useAuth';
import { useThemeStore } from './store/themeStore';
import { Logo } from './components/Logo';
import { auth } from './firebase';
import { getPendingInviteForMe } from './lib/api';
import { NPSModal } from './components/feedback/NPSModal';
import { checkNpsEligibility, recordNpsState } from './lib/feedback';
import { queryClient } from './lib/queryClient';

function NoAccessScreen() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [inviteId, setInviteId] = useState<string | null>(null);

  useEffect(() => {
    getPendingInviteForMe()
      .then((invite) => {
        if (invite?.inviteId) {
          setInviteId(invite.inviteId);
        }
      })
      .catch(() => { /* no-op — stay on no-access screen */ })
      .finally(() => setChecking(false));
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-white dark:bg-bg-deep flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-bg-deep flex items-center justify-center p-8">
      <div className="w-full max-w-sm text-center">
        <div className="flex justify-center mb-8">
          <Logo className="h-10 w-auto text-gray-900 dark:text-white" />
        </div>
        {inviteId ? (
          <>
            <h1 className="text-title font-bold text-gray-900 dark:text-white mb-3 font-display">
              You have a pending invite
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-meta leading-relaxed mb-8">
              An invite is waiting for your account. Click below to accept it and join your workspace.
            </p>
            <div className="space-y-4">
              <button
                onClick={() => navigate(`/accept-invite/${inviteId}`)}
                className="block w-full py-2.5 bg-primary text-white rounded-full text-meta font-medium hover:bg-primary/90 transition-colors"
              >
                Accept invite & join workspace
              </button>
              <button
                onClick={() => void signOut(auth)}
                className="block w-full py-2.5 text-gray-500 dark:text-gray-400 text-meta hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Sign out
              </button>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-title font-bold text-gray-900 dark:text-white mb-3 font-display">
              No workspace found
            </h1>
            <p className="text-gray-500 dark:text-gray-400 text-meta leading-relaxed mb-8">
              Your account isn't linked to an OctoPM workspace yet. Ask your admin to send
              you an invite link, or reach out to us to get started.
            </p>
            <div className="space-y-4">
              <a
                href="mailto:support@octo.pm"
                className="block w-full py-2.5 bg-gray-800 dark:bg-primary text-white rounded-full text-meta font-medium hover:bg-gray-900 dark:hover:bg-primary/90 transition-colors text-center"
              >
                Contact support@octo.pm
              </a>
              <button
                onClick={() => void signOut(auth)}
                className="block w-full py-2.5 text-gray-500 dark:text-gray-400 text-meta hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Sign out
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const NPS_DELAY_MS = 10_000; // 10 seconds

function NPSGate() {
  const [open, setOpen] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    let cancelled = false;

    void checkNpsEligibility().then((eligible) => {
      if (!eligible || cancelled || firedRef.current) return;
      const id = setTimeout(() => {
        if (!cancelled && !firedRef.current) {
          firedRef.current = true;
          setOpen(true);
        }
      }, NPS_DELAY_MS);
      return () => clearTimeout(id);
    });

    return () => { cancelled = true; };
  }, []);

  const handleClose = (action: 'submitted' | 'later' | 'dismissed') => {
    setOpen(false);
    if (action === 'later') void recordNpsState('snoozed');
    if (action === 'dismissed') void recordNpsState('dismissed');
    // 'submitted' → backend already records it via POST /feedback
  };

  return <NPSModal isOpen={open} onClose={handleClose} />;
}

function AuthGuard() {
  const { user, loading, pendingClaims } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-bg-deep flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (pendingClaims) {
    return <NoAccessScreen />;
  }

  return <Outlet />;
}

function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme } = useThemeStore();

  useEffect(() => {
    const html = document.documentElement;
    if (theme === 'dark') {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }
  }, [theme]);

  return <>{children}</>;
}

function PageViewTracker() {
  const location = useLocation();
  const pageStartRef = useRef<{ route: string; ts: number } | null>(null);

  useEffect(() => {
    const prev = pageStartRef.current;
    if (prev) {
      track('page.time_spent', 'page_view', {
        route: prev.route,
        durationMs: Date.now() - prev.ts,
      });
    }
    setTelemetryRoute(location.pathname);
    track('page.viewed', 'page_view', { route: location.pathname });
    pageStartRef.current = { route: location.pathname, ts: Date.now() };
  }, [location.pathname]);

  return null;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PageViewTracker />
        <ThemeProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/accept-invite/:inviteId" element={<AcceptInvite />} />
            <Route path="/integrations/:provider/callback" element={<OAuthCallback />} />
            <Route path="/integrations/personal/:provider/callback" element={<OAuthCallback personal />} />

            {/* Protected routes */}
            <Route element={<AuthGuard />}>
              <Route element={<><NPSGate /><AppShell /></>}>
                <Route index element={<RoleDashboard />} />
                <Route path="projects/:id" element={<ProjectDashboard />} />
                <Route path="settings" element={<Settings />} />
                <Route path="portfolio" element={<PortfolioPage />} />
                <Route path="my-work" element={<MyWorkPage />} />
                <Route path="insights" element={<InsightsPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="team" element={<TeamPage />} />
                <Route path="launches" element={<LaunchesPage />} />
                <Route path="support" element={<Support />} />
              </Route>
            </Route>

            {/* Catch-all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
